import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Two axios clients:
//   `pub`      → no credentials, used for anonymous public endpoints.
//                The platform ingress sends `Allow-Origin: *`, which the browser
//                REJECTS if the request carries credentials — so keep these clean.
//   `authed`   → sends cookies, used only for auth + user-scoped endpoints.
//                When AUTH_MODE=email you must set an explicit CORS_ORIGINS in
//                backend/.env (see DESIGN.md §3).
const pub = axios.create();
const authed = axios.create({ withCredentials: true });

// -------- Analysis / vendors / sample --------
export async function analyzeCsv(file) {
  const form = new FormData();
  form.append("file", file);
  // Use authed so the analysis is attributed to the logged-in user when auth is on.
  const res = await authed.post(`${API}/analyze`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function getSampleCsv() {
  const res = await pub.get(`${API}/sample`);
  return res.data.csv;
}

export async function diffCsvs(oldFile, newFile) {
  const form = new FormData();
  form.append("old_file", oldFile);
  form.append("new_file", newFile);
  const res = await pub.post(`${API}/diff`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function listVendors() {
  const res = await pub.get(`${API}/vendors`);
  return res.data.vendors;
}

export async function importVendor(vendorId, file) {
  const form = new FormData();
  form.append("file", file);
  const res = await pub.post(`${API}/import/${vendorId}`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function downloadPdf(analysis, options = {}) {
  const payload = { ...analysis, options };
  const res = await pub.post(`${API}/report/pdf`, payload, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `shadow-firewall-report-${(analysis.id || "analysis").slice(0, 8)}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function proposeReorder(rules) {
  const res = await pub.post(`${API}/reorder`, { rules });
  return res.data;
}

export async function simulateTraffic(rules, source, destination, protocol, port) {
  const res = await pub.post(`${API}/simulate`, {
    rules, source, destination, protocol, port,
  });
  return res.data;
}

// -------- Auth (always credentialed) --------
export async function getAuthConfig() {
  // Config lookup is anonymous — use `pub` so it works even if CORS+credentials
  // are misconfigured. It never returns sensitive data.
  const res = await pub.get(`${API}/auth/config`);
  return res.data;
}

export async function authRegister(email, password, name) {
  const res = await authed.post(`${API}/auth/register`, { email, password, name });
  return res.data;
}

export async function authLogin(email, password) {
  const res = await authed.post(`${API}/auth/login`, { email, password });
  return res.data;
}

export async function authLogout() {
  await authed.post(`${API}/auth/logout`);
}

export async function authMe() {
  const res = await authed.get(`${API}/auth/me`);
  return res.data;
}

// -------- History (always credentialed) --------
export async function listAnalyses() {
  const res = await authed.get(`${API}/analyses`);
  return res.data.items;
}

export async function getAnalysis(id) {
  const res = await authed.get(`${API}/analyses/${id}`);
  return res.data;
}

export async function deleteAnalysis(id) {
  await authed.delete(`${API}/analyses/${id}`);
}

// -------- Error formatter --------
export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
